import React, { useState, useRef, useLayoutEffect, useSyncExternalStore, useEffect } from "react";
import { Box, Tooltip } from "@mui/material";
import DeleteSweepOutlinedIcon from "@mui/icons-material/DeleteSweepOutlined";
import { Formik } from "formik";
import VoiceSearchInput from "../../../uikit/VoiceSearchInput";
import { getDraftInputText, setDraftInputText, subscribeConversationState } from "../../../utils/AIPrescription";

export type PrescribeMode = "agentic" | "transcribe";

interface SearchInputSectionProps {
  showResults: boolean;
  voiceSearchCountdown: number | null;
  onSearch: (text: string, options?: { vttContent?: string }) => void;
  onVoiceResult: (text: string) => void;
  primaryColor: string;
  formikRef?: React.RefObject<any>;
  /** Whether the input bar is shown. Controlled by the page so the results
   *  area can reclaim the space when the bar is hidden. */
  barOpen: boolean;
  onToggleBar: () => void;
  /** True while a previous turn is still in flight (orchestration hasn't sent
   *  its "result"/error back yet). Disables the input + mic/send/upload
   *  buttons so the user can't fire a second overlapping turn — critical for
   *  send-to-pharmacy requests, which must never be submitted twice. */
  disabled?: boolean;
  /** Resets the chat/draft on both sides without ending the login or changing
   *  the selected patient. Optional so callers that don't need this control
   *  (if any are ever added) can omit it. Can be async; awaits backend confirmation. */
  onClearConversation?: () => Promise<void> | void;
}

const SearchInputSection: React.FC<SearchInputSectionProps> = ({
  showResults,
  voiceSearchCountdown,
  onSearch,
  onVoiceResult,
  formikRef,
  barOpen,
  primaryColor,
  disabled = false,
  onClearConversation,
}) => {
  const [vttContent, setVttContent] = useState<string | null>(null);
  /** Agentic/Voice is the default; Transcribe shows the VTT upload control. */
  const [prescribeMode, setPrescribeMode] = useState<PrescribeMode>("agentic");

  // Subscribe to persisted draft input so Formik initializes with any previously-typed/voiced text
  const persistedDraftInput = useSyncExternalStore(subscribeConversationState, getDraftInputText);

  console.log('[DRAFT-DIAGNOSTIC] SearchInputSection render: persistedDraftInput =', JSON.stringify(persistedDraftInput));

  // The bar is a FIXED footer (the app window scrolls, so sticky/flow-bottom
  // drift away). We measure this in-flow spacer to keep the fixed bar aligned
  // to the content column, re-measuring on resize / sidebar toggle.
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [barBox, setBarBox] = useState<{ left: number; width: number }>({
    left: 0,
    width: 0,
  });

  useLayoutEffect(() => {
    console.log('[DRAFT-DIAGNOSTIC] SearchInputSection: MOUNTED, persistedDraftInput =', JSON.stringify(persistedDraftInput));
    const el = anchorRef.current;
    if (!el) return;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setBarBox({ left: r.left, width: r.width });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      console.log('[DRAFT-DIAGNOSTIC] SearchInputSection: UNMOUNTING');
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [persistedDraftInput]);

  const initialValues = {
    prescriptionText: persistedDraftInput || "",
  };

  console.log('[DRAFT-DIAGNOSTIC] SearchInputSection: initialValues =', JSON.stringify(initialValues));

  const handleSubmit = (values: any) => {
    console.log('[DRAFT-DIAGNOSTIC] SearchInputSection.handleSubmit called with values.prescriptionText =', JSON.stringify(values.prescriptionText));
    if (disabled) return;
    const raw = values.prescriptionText?.trim()
      ? values.prescriptionText.trim().replace(/\s+/g, " ")
      : "";
    const vtt = vttContent?.trim();
    if (!raw && !vtt) return;
    onSearch(raw, vtt ? { vttContent: vttContent! } : undefined);
    setVttContent(null);
    // Note: Draft is intentionally NOT cleared for typed input submission.
    // Original behavior: field is left intact so user can edit/re-send the same message.
    // Draft follows the same pattern: persists until explicit clear or patient change.
  };

  const handleChange = (e: any, formProps: any) => {
    formProps.handleChange(e);
    // Extract the new value from the event directly (not from formProps.values, which may still be stale)
    const newValue = e.target.value;
    console.log('[DRAFT-DIAGNOSTIC] SearchInputSection.handleChange: user typed, newValue =', JSON.stringify(newValue), '| Formik.values.prescriptionText =', JSON.stringify(formProps.values.prescriptionText));
    setDraftInputText(newValue);
  };

  const handleKeyDown = (e: any, formProps: any) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (disabled) return;
      const currentValue = formProps.values.prescriptionText;
      const raw = currentValue?.trim()
        ? currentValue.trim().replace(/\s+/g, " ")
        : "";
      const vtt = vttContent?.trim();
      if (!raw && !vtt) return;
      onSearch(raw, vtt ? { vttContent: vttContent! } : undefined);
      setVttContent(null);
    }
  };

  const handleSearchFromInput = (
    searchText: string,
    options?: { vttContent?: string }
  ) => {
    if (disabled) return;
    const raw = searchText?.trim()
      ? searchText.trim().replace(/\s+/g, " ")
      : "";
    const vtt = (options?.vttContent ?? vttContent)?.trim();
    if (!raw && !vtt) return;
    const payloadVtt = options?.vttContent ?? vttContent;
    onSearch(raw, payloadVtt?.trim() ? { vttContent: payloadVtt } : undefined);
    setVttContent(null);
  };

  const placeholder =
    prescribeMode === "transcribe"
      ? "Upload a prescription file..."
      : "Prescribe medication...";

  return (
    <>
      <div
        ref={anchorRef}
        style={{ width: "100%", height: 124, marginTop: "auto" }}
      />

      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: `${barBox.left}px`,
          width: barBox.width ? `${barBox.width}px` : "100%",
          zIndex: 1200,
          display: "flex",
          justifyContent: "center",
          backgroundColor: barOpen ? "#ffffff" : "transparent",
          padding: { xs: "8px 8px 10px", md: "10px 0 14px" },
          boxSizing: "border-box",
          pointerEvents: barOpen ? "auto" : "none",
        }}
      >
        <Box
          sx={{
            width: { xs: "100%", md: showResults ? "97%" : "80%" },
            maxWidth: { xs: "none", md: "980px" },
            display: "flex",
            alignItems: "center",
          }}
        >
          {barOpen && (
            <Box sx={{ width: "100%", minWidth: 0, pointerEvents: "auto" }}>
              <Formik
                initialValues={initialValues}
                enableReinitialize={true}
                onSubmit={handleSubmit}
                innerRef={formikRef || undefined}
              >
                {(formProps) => (
                  <form
                    onSubmit={formProps.handleSubmit}
                    style={{ width: "100%" }}
                  >
                    <Box sx={{ width: "100%", position: "relative" }}>
                      <VoiceSearchInput
                        voiceSearchCountdown={voiceSearchCountdown}
                        id="prescriptionText"
                        name="prescriptionText"
                        type="text"
                        className="input"
                        disabled={disabled}
                        multiline={true}
                        minRows={1}
                        maxRows={10}
                        placeholder={placeholder}
                        prescribeMode={prescribeMode}
                        onPrescribeModeChange={setPrescribeMode}
                        onVoiceResult={onVoiceResult}
                        onChange={(e: any) => handleChange(e, formProps)}
                        onSearch={handleSearchFromInput}
                        onVttContentChange={setVttContent}
                        pendingVttContent={vttContent}
                        onKeyDown={(e: any) => handleKeyDown(e, formProps)}
                        sx={{ width: "100%" }}
                      />
                    </Box>
                  </form>
                )}
              </Formik>
              {onClearConversation && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    pt: { xs: "6px", md: "8px" },
                    pr: { xs: 0, md: "12px" },
                  }}
                >
                  <Tooltip
                    title={
                      disabled
                        ? "Wait until the current request finishes"
                        : "Clear this conversation and start over. The patient stays selected."
                    }
                    placement="top"
                  >
                    <span>
                      <Box
                        component="button"
                        type="button"
                        onClick={onClearConversation}
                        disabled={disabled}
                        aria-label="Clear conversation"
                        sx={{
                          all: "unset",
                          boxSizing: "border-box",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "8px",
                          padding: "4px 10px 4px 4px",
                          borderRadius: "20px",
                          cursor: disabled ? "default" : "pointer",
                          color: disabled ? `${primaryColor}88` : primaryColor,
                          fontFamily: "inherit",
                          fontSize: "12px",
                          fontWeight: 600,
                          lineHeight: 1,
                          opacity: disabled ? 0.55 : 1,
                          transition: "background-color 0.15s ease, color 0.15s ease",
                          "&:hover": disabled
                            ? undefined
                            : { backgroundColor: `${primaryColor}14` },
                          "&:focus-visible": {
                            outline: `2px solid ${primaryColor}`,
                            outlineOffset: 1,
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 26,
                            height: 26,
                            borderRadius: "50%",
                            backgroundColor: `${primaryColor}1a`,
                            color: "inherit",
                            flex: "none",
                          }}
                        >
                          <DeleteSweepOutlinedIcon sx={{ fontSize: 16 }} />
                        </Box>
                        Clear conversation
                      </Box>
                    </span>
                  </Tooltip>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default SearchInputSection;
